import React, { FC, useMemo } from 'react';
import styled from 'styled-components';
import Colours from '../../../design-system/colours';
import { AggregatedScan } from '../../../common/model/dto/hormone-pattern/aggregated-scan';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import OvHormonePatternTooltip from '../molecules/OvHormonePatternTooltip';
import OvHormonePatternLegend from '../molecules/OvHormonePatternLegend';
import { useTranslation } from 'react-i18next';
import MathUtils from '../../../common/utils/services/math-utils';
import { Constants } from '../../../common/constants';

type OvHormonePatternScansChartProps = {
  scans: AggregatedScan[];
};

const OvHormonePatternScansChart: FC<OvHormonePatternScansChartProps> = ({
  scans,
}) => {
  const { t } = useTranslation();
  const chartData: (AggregatedScan & { index: number })[] = useMemo(
    () =>
      (scans || []).map((scan, index) => ({
        index: index + 1,
        day: scan.day,
        lh: scan.lh
          ? MathUtils.normalizeValue(
              scan.lh,
              Constants.LH_MIN,
              Constants.LH_MAX
            )
          : undefined,
        pg: scan.pg
          ? MathUtils.normalizeValue(
              scan.pg,
              Constants.PG_MIN,
              Constants.PG_MAX
            )
          : undefined,
        lr_e3g: scan.lr_e3g
          ? MathUtils.normalizeValue(
              scan.lr_e3g,
              Constants.LR_E3G_MIN,
              Constants.LR_E3G_MAX
            )
          : undefined,
        lh_percentage: scan.lh_percentage
          ? MathUtils.normalizeValue(scan.lh_percentage, 0, 100)
          : undefined,
        pg_percentage: scan.pg_percentage
          ? MathUtils.normalizeValue(scan.pg_percentage, 0, 100)
          : undefined,
        lr_e3g_percentage: scan.lr_e3g_percentage
          ? MathUtils.normalizeValue(scan.lr_e3g_percentage, 0, 100)
          : undefined,
      })),
    [scans]
  );

  return (
    <Container>
      <OvHormonePatternLegend />
      <ResponsiveContainer width="100%" height={350}>
        <LineChart
          data={chartData}
          margin={{ top: 16, right: 24, bottom: 8, left: 8 }}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="index"
            type="number"
            domain={[1, chartData.length || 1]}
            tick={{ fontSize: 12, fill: Colours.OV_BASE }}
          />
          <YAxis
            domain={[0, 100]}
            tickSize={2}
            tick={{ fontSize: 10, fill: Colours.OV_BASE }}
            label={{
              value: t('hormonePattern.chart.percentage'),
              angle: -90,
              position: 'center',
              offset: 15,
              fill: Colours.OV_BASE,
              width: 200,
              height: 20,
              fontSize: 12,
              lineHeight: 1.5,
            }}
          />
          <Tooltip
            content={(props: any) => (
              <OvHormonePatternTooltip {...props} data={chartData} />
            )}
          />
          <Line
            type="linear"
            dataKey="lh_percentage"
            name="LH"
            stroke={Colours.OV_DARK_GREEN}
            strokeWidth={2}
            dot={{ r: 2.5 }}
            isAnimationActive={false}
            connectNulls={false}
          />
          <Line
            type="linear"
            dataKey="pg_percentage"
            name="PG"
            stroke={Colours.POST_OVULATION}
            strokeWidth={2}
            dot={{ r: 2.5 }}
            isAnimationActive={false}
            connectNulls={false}
          />
          <Line
            type="linear"
            dataKey="lr_e3g_percentage"
            name="LR_E3G"
            stroke={Colours.LIME_GREEN}
            strokeWidth={2}
            dot={{ r: 2.5 }}
            isAnimationActive={false}
            connectNulls={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </Container>
  );
};

export default OvHormonePatternScansChart;

const Container = styled.div`
  width: 100%;
  height: fit-content;
`;
